import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CreateContactData, FamilyContact } from '@/hooks/useContacts';
import { FamilyMember } from '@/hooks/useFamily';

interface CreateContactFormProps {
  familyMembers: FamilyMember[];
  onSubmit: (contactData: CreateContactData) => void;
  onCancel: () => void;
  loading?: boolean;
  initialData?: Partial<FamilyContact>;
}

const contactTypeOptions = [
  { value: 'doctor', label: 'Médecin' },
  { value: 'pediatrician', label: 'Pédiatre' },
  { value: 'dentist', label: 'Dentiste' },
  { value: 'school', label: 'École' },
  { value: 'teacher', label: 'Enseignant(e)' },
  { value: 'babysitter', label: 'Baby-sitter' },
  { value: 'nanny', label: 'Nounou' },
  { value: 'nannies_helpers', label: 'Nounous, aide' },
  { value: 'daycare', label: 'Crèche' },
  { value: 'family_friend', label: 'Ami de la famille' },
  { value: 'neighbor', label: 'Voisin(e)' },
  { value: 'emergency', label: 'Urgence' },
  { value: 'pharmacy', label: 'Pharmacie' },
  { value: 'sports_club', label: 'Club de sport' },
  { value: 'other', label: 'Autre' },
];

export const CreateContactForm = ({ 
  familyMembers, 
  onSubmit, 
  onCancel, 
  loading, 
  initialData 
}: CreateContactFormProps) => {
  const [formData, setFormData] = useState<CreateContactData>({
    first_name: initialData?.first_name || '',
    last_name: initialData?.last_name || '',
    organization: initialData?.organization || '',
    contact_type: initialData?.contact_type || 'other',
    phone_primary: initialData?.phone_primary || '',
    phone_secondary: initialData?.phone_secondary || '',
    email: initialData?.email || '',
    address: initialData?.address || '',
    notes: initialData?.notes || '',
    child_member_id: initialData?.child_member_id || '',
    is_emergency_contact: initialData?.is_emergency_contact || false,
    availability_notes: initialData?.availability_notes || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clean up empty strings
    const cleanedData = {
      ...formData,
      organization: formData.organization || undefined,
      phone_primary: formData.phone_primary || undefined,
      phone_secondary: formData.phone_secondary || undefined,
      email: formData.email || undefined,
      address: formData.address || undefined,
      notes: formData.notes || undefined,
      child_member_id: formData.child_member_id || undefined,
      availability_notes: formData.availability_notes || undefined,
    };
    
    onSubmit(cleanedData);
  };

  const updateField = (field: keyof CreateContactData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const children = familyMembers.filter(member => !member.profile_id);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {initialData ? 'Modifier le contact' : 'Nouveau contact'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic information */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">Prénom *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => updateField('first_name', e.target.value)}
                required
                placeholder="Jean"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Nom *</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => updateField('last_name', e.target.value)}
                required
                placeholder="Dupont"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="organization">Organisation / Établissement</Label>
            <Input
              id="organization"
              value={formData.organization}
              onChange={(e) => updateField('organization', e.target.value)}
              placeholder="Hôpital Saint-Louis, École Primaire..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact_type">Type de contact *</Label>
            <Select 
              value={formData.contact_type} 
              onValueChange={(value) => updateField('contact_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un type" />
              </SelectTrigger>
              <SelectContent>
                {contactTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Child association */}
          {children.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="child_member">Contact spécifique à un enfant</Label>
              <Select 
                value={formData.child_member_id} 
                onValueChange={(value) => updateField('child_member_id', value === 'none' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Contact général de famille" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Contact général de famille</SelectItem>
                  {children.map((child) => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.child_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Contact details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone_primary">Téléphone principal</Label>
              <Input
                id="phone_primary"
                type="tel"
                value={formData.phone_primary}
                onChange={(e) => updateField('phone_primary', e.target.value)}
                placeholder="01 23 45 67 89"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone_secondary">Téléphone secondaire</Label>
              <Input
                id="phone_secondary"
                type="tel"
                value={formData.phone_secondary}
                onChange={(e) => updateField('phone_secondary', e.target.value)}
                placeholder="06 12 34 56 78"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="contact@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Adresse</Label>
            <Textarea
              id="address"
              value={formData.address}
              onChange={(e) => updateField('address', e.target.value)}
              placeholder="123 Rue de la Paix, 75001 Paris"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="availability_notes">Disponibilités / Horaires</Label>
            <Textarea
              id="availability_notes"
              value={formData.availability_notes}
              onChange={(e) => updateField('availability_notes', e.target.value)}
              placeholder="Lundi-Vendredi 9h-17h, Urgences 24h/24..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => updateField('notes', e.target.value)}
              placeholder="Informations supplémentaires..."
              rows={3}
            />
          </div>

          {/* Emergency contact flag */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is_emergency_contact"
              checked={formData.is_emergency_contact}
              onCheckedChange={(checked) => updateField('is_emergency_contact', !!checked)}
            />
            <Label htmlFor="is_emergency_contact" className="text-sm font-medium">
              Contact d'urgence
            </Label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Enregistrement...' : initialData ? 'Modifier' : 'Créer le contact'}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Annuler
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};